import { TooltipProvider } from "@radix-ui/react-tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Compass } from "lucide-react";
import { StrictMode } from "react";
import { createRoot, type Root } from "react-dom/client";
import { createBrowserRouter, Link, RouterProvider } from "react-router";
import { Toaster } from "sonner";
import { PublicTrackingPage, trackingHostSlug } from "./tracking-page/PublicTracking";
import { TrackingPageComposerPage } from "./pages/TrackingPageComposer";
import { PrivacyRequestsPage } from "./pages/PrivacyRequests";
import { ShopifyConnectPage } from "./pages/ShopifyConnect";
import { RegisterPage } from "./pages/Register";
import { SalesRoomPage } from "./pages/SalesRoom";
import { OpenInvoicesPage } from "./pages/OpenInvoices";
import { AppShell, PageHeader } from "./components/AppShell";
import { Button, EmptyState } from "./components/ui";
import { CustomersPage, FfnMerchantsPage, StaffPage } from "./pages/Admin";
import { ChannelsPage } from "./pages/Channels";
import { AdminShippingPage } from "./pages/AdminShipping";
import { AdminAddressPage } from "./pages/AdminAddress";
import { AdminNewsPage } from "./pages/AdminNews";
import { AdminChangelogPage } from "./pages/AdminChangelog";
import { AdminSystemPage } from "./pages/AdminSystem";
import { AdminKpisPage } from "./pages/AdminKpis";
import { PartnersPage } from "./pages/Partners";
import { AdminPartnersPage } from "./pages/AdminPartners";
import { AdminReturnsPage } from "./pages/AdminReturns";
import { AdminModulesPage } from "./pages/AdminModules";
import { ModulesPage } from "./pages/Modules";
import { ReturnsPage } from "./pages/Returns";
import { ChannelDetailPage } from "./pages/ChannelDetail";
import { ShippingRulesPage } from "./pages/ShippingRules";
import { LoginPage, MfaPage, VerifyPage } from "./pages/Login";
import { SecurityPage } from "./pages/Security";
import { AuditPage } from "./pages/Audit";
import { IntegrationsPage } from "./pages/Integrations";
import { AppsPage } from "./pages/Apps";
import { CostsPage } from "./pages/Costs";
import { ProfitPage } from "./pages/Profit";
import { ProductionPage } from "./pages/Production";
import { ProductRulesPage } from "./pages/ProductRules";
import { ValidationSettingsPage } from "./pages/ValidationSettings";
import { DeliveryWidgetPage } from "./pages/DeliveryWidget";
import { ModuleGate } from "./components/ModuleGate";
import { PublicAddressPage } from "./pages/PublicAddress";
import { CustomerCommsPage } from "./pages/CustomerComms";
import { PurchasingPage } from "./pages/Purchasing";
import { B2bPage } from "./pages/B2b";
import { FbaPage } from "./pages/Fba";
import { ConnectAuthorizePage } from "./pages/ConnectAuthorize";
import { AiAssistantPage } from "./pages/AiAssistant";
import { AdminCarrierAccountsPage, CarrierAccountsPage } from "./pages/CarrierAccounts";
import { TrackingPage } from "./pages/Tracking";
import { SettingsOverviewPage } from "./pages/SettingsOverview";
import { StockSyncPage } from "./components/StockSync";
import { FfnApprovalPage } from "./pages/FfnLink";
import { TeamPage } from "./pages/Team";
import { WarehousePage } from "./pages/Warehouse";
import { DashboardPage } from "./pages/Dashboard";
import { ActionItemsPage } from "./pages/ActionItems";
import { NotificationsPage } from "./pages/Notifications";
import { PerformancePage } from "./pages/Performance";
import { InboundsPage } from "./pages/Inbounds";
import { OrdersPage } from "./pages/Orders";
import { ProductsPage } from "./pages/Products";
import { RulesPage } from "./pages/Rules";
import { StockPage } from "./pages/Stock";
import "./styles.css";
import { initI18n } from "./lib/i18n";
import { installDomGuard } from "./lib/dom-guard";
import { ErrorPage } from "./components/ErrorPage";

import { t } from "@/lib/i18n";
const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: true } },
});

const NotFound = () => (
  <div>
    <PageHeader title={t("Seite nicht gefunden")} />
    <EmptyState icon={<Compass className="size-5" />} title={t("Hier ist nichts")} action={<Link to="/"><Button variant="primary">{t("Zur Übersicht")}</Button></Link>}>
      {t("Der Link ist veraltet oder falsch. Mit ⌘K findest du jede Seite und jeden Artikel.")}
    </EmptyState>
  </div>
);

// Eigene Domain eines Kunden: nur die Tracking-Seite (Server kennzeichnet index.html)
const hostSlug = trackingHostSlug();
installDomGuard();
const router = hostSlug ? createBrowserRouter([
  { path: "/", element: <PublicTrackingPage hostSlug={hostSlug} />, errorElement: <ErrorPage /> },
  { path: "/:token", element: <PublicTrackingPage hostSlug={hostSlug} />, errorElement: <ErrorPage /> },
]) : createBrowserRouter([{ errorElement: <ErrorPage />, children: [
  { path: "/login", element: <LoginPage /> },
  { path: "/login/verify", element: <VerifyPage /> },
  { path: "/login/mfa", element: <MfaPage /> },
  { path: "/ffn-freigabe", element: <FfnApprovalPage /> },
  // Shop aus der Logiful-App in Shopify: neues Konto oder mit bestehendem verknüpfen
  { path: "/shop-verbinden", element: <ShopifyConnectPage /> },
  // Interessenten: Angebot aus dem Sales Deal Manager (Link aus der Angebotsmail; /sales-room/ wie bisher in Retool)
  { path: "/angebot/:token", element: <SalesRoomPage /> },
  { path: "/sales-room/:token", element: <SalesRoomPage /> },
  // Neukunden: Konto selbst anlegen (startet im Testbetrieb)
  { path: "/registrieren", element: <RegisterPage /> },
  { path: "/connect/authorize", element: <ConnectAuthorizePage /> },
  // Endkunden: Adressklärung über den persönlichen Link (ohne Anmeldung)
  { path: "/adresse/:token", element: <PublicAddressPage /> },
  // Endkunden: Tracking-Seite eines Shops (Suche bzw. persönlicher Link)
  { path: "/t/:slug", element: <PublicTrackingPage /> },
  { path: "/t/:slug/:token", element: <PublicTrackingPage /> },
  {
    path: "/",
    element: <AppShell />,
    errorElement: <ErrorPage />,
    // Fehler einer Seite innerhalb der Navigation anzeigen – Menü bleibt bedienbar
    children: [{ errorElement: <ErrorPage inShell />, children: [
      { index: true, element: <DashboardPage /> },
      { path: "todo", element: <ActionItemsPage /> },
      { path: "invoices", element: <OpenInvoicesPage /> },
      { path: "performance", element: <ModuleGate module="analytics" feature="performance" title={t("Leistung")}><PerformancePage /></ModuleGate> },
      { path: "costs", element: <ModuleGate module="analytics" feature="costs" title={t("Kosten")}><CostsPage /></ModuleGate> },
      { path: "production", element: <ModuleGate module="production" feature="orders" title={t("Produktion")}><ProductionPage /></ModuleGate> },
      { path: "profit", element: <ModuleGate module="profit" feature="margins" title={t("Profit Center")}><ProfitPage /></ModuleGate> },
      { path: "partners", element: <PartnersPage /> },
      { path: "admin/partners", element: <AdminPartnersPage /> },
      { path: "products", element: <ProductsPage /> },
      { path: "orders", element: <OrdersPage /> },
      { path: "channels", element: <ChannelsPage /> },
      { path: "channels/:channelId", element: <ChannelDetailPage /> },
      { path: "connect/shopify", element: <ChannelsPage /> },
      { path: "stock", element: <StockPage /> },
      { path: "inbounds", element: <InboundsPage /> },
      { path: "fba", element: <ModuleGate module="fba" feature="prefba" title={t("Pre-FBA")}><FbaPage /></ModuleGate> },
      { path: "b2b", element: <ModuleGate module="b2b" feature="b2b_orders" title={t("B2B-Lieferungen")}><B2bPage /></ModuleGate> },
      { path: "purchasing", element: <ModuleGate module="purchasing" feature="purchase_orders" title={t("Einkauf")}><PurchasingPage /></ModuleGate> },
      { path: "returns", element: <ReturnsPage /> },
      { path: "tracking", element: <ModuleGate module="tracking" feature="delivery_status" title={t("Track & Trace")}><TrackingPage /></ModuleGate> },
      { path: "tracking/page", element: <ModuleGate module="tracking_page" feature="page" title={t("Tracking-Seite")}><TrackingPageComposerPage /></ModuleGate> },
      { path: "rules", element: <RulesPage /> },
      { path: "rules/shipping", element: <ShippingRulesPage /> },
      { path: "rules/products", element: <ProductRulesPage /> },
      { path: "settings/validation", element: <ValidationSettingsPage /> },
      { path: "admin/shipping", element: <AdminShippingPage /> },
      { path: "admin/carrier-accounts", element: <AdminCarrierAccountsPage /> },
      { path: "settings/carrier-accounts", element: <CarrierAccountsPage /> },
      { path: "admin/address", element: <AdminAddressPage /> },
      { path: "admin/returns", element: <AdminReturnsPage /> },
      { path: "settings", element: <SettingsOverviewPage /> },
      { path: "settings/team", element: <TeamPage /> },
      { path: "settings/warehouse", element: <WarehousePage /> },
      { path: "settings/modules", element: <ModulesPage /> },
      { path: "settings/notifications", element: <NotificationsPage /> },
      { path: "settings/security", element: <SecurityPage /> },
      { path: "settings/audit", element: <AuditPage /> },
      { path: "settings/privacy", element: <PrivacyRequestsPage /> },
      { path: "settings/stock-sync", element: <StockSyncPage /> },
      { path: "settings/customer-comms", element: <ModuleGate module="customer_comms" title={t("Kundenkommunikation")}><CustomerCommsPage /></ModuleGate> },
      { path: "settings/ai", element: <ModuleGate module="ai" feature="assistant" title={t("KI-Assistent")}><AiAssistantPage /></ModuleGate> },
      { path: "settings/apps", element: <ModuleGate module="apps" title={t("Tool-Anbindungen")}><AppsPage /></ModuleGate> },
      { path: "settings/integrations", element: <ModuleGate module="api" title={t("Schnittstelle")}><IntegrationsPage /></ModuleGate> },
      { path: "settings/delivery-widget", element: <ModuleGate module="shop_widgets" feature="delivery_promise" title={t("Lieferzeit im Shop")}><DeliveryWidgetPage /></ModuleGate> },
      { path: "admin/news", element: <AdminNewsPage /> },
      { path: "admin/changelog", element: <AdminChangelogPage /> },
      { path: "admin/system", element: <AdminSystemPage /> },
      { path: "admin/kpis", element: <AdminKpisPage /> },
      { path: "admin/modules", element: <AdminModulesPage /> },
      { path: "admin/customers", element: <CustomersPage /> },
      { path: "admin/team", element: <StaffPage /> },
      { path: "admin/ffn", element: <FfnMerchantsPage /> },
      { path: "*", element: <NotFound /> },
    ] }],
  },
] }]);

// Beim Hot-Reload wird dieses Modul erneut ausgeführt – vorhandene Root wiederverwenden statt doppelt zu mounten
const w = window as typeof window & { __lcRoot?: Root };
w.__lcRoot ??= createRoot(document.getElementById("root")!);
// Sprache (und ggf. englischen Katalog) vor dem ersten Rendern laden
await initI18n();
w.__lcRoot.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <RouterProvider router={router} />
        <Toaster position="bottom-right" toastOptions={{ className: "!rounded-xl !border-border !bg-surface !text-fg" }} />
      </TooltipProvider>
    </QueryClientProvider>
  </StrictMode>,
);
